<template>
  <div class="min-h-screen bg-gray-50/50 pb-3 md:p-4">
    <div
      v-if="isLoadingDetail || savingNewDetail"
      class="flex flex-col items-center justify-center min-h-[400px]"
    >
      <Loader />
      <p class="mt-4 text-slate-500 animate-pulse">Loading data...</p>
    </div>

    <div
      v-else-if="freebetData && !isLoadingDetail"
      class="max-w-full mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-3">
        <div
          class="flex flex-col md:flex-row md:items-center justify-between gap-2.5"
        >
          <div class="flex items-start gap-4">
            <div>
              <p
                class="text-[12px] font-bold text-slate-700 uppercase tracking-widest"
              >
                External ID
              </p>
              <h3 class="text-xl text-blue-600 font-black">
                #{{ freebetData.external_id }}
              </h3>
            </div>
          </div>

          <div
            class="flex-1 md:px-8 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0"
          >
            <h1 class="text-2xl font-bold text-gray-900 leading-tight">
              {{ freebetData.title || "No Title" }}
            </h1>
            <p class="text-gray-500 mt-1 leading-relaxed text-sm">
              {{
                freebetData.description ||
                "No description provided for this freebet."
              }}
            </p>
          </div>

          <div class="flex flex-col items-end gap-3">
            <div
              class="px-3 py-1 rounded-full text-[10px] font-black flex items-center gap-2 border"
              :class="
                freebetData.is_enabled
                  ? 'bg-green-50 text-green-600 border-green-100'
                  : 'bg-red-50 text-red-600 border-red-100'
              "
            >
              <span
                class="w-2 h-2 rounded-full"
                :class="freebetData.is_enabled ? 'bg-green-500' : 'bg-red-500'"
              ></span>
              {{ freebetData.is_enabled ? "ACTIVE" : "INACTIVE" }}
            </div>

            <div v-if="!freebetData.campaign_id" class="flex gap-2">
              <button
                @click="cloneFreebets"
                class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
              >
                <DocumentDuplicateIcon class="block w-7 h-7" />
                Clone
              </button>
              <router-link
                :to="{
                  name: 'FreebetsEdit',
                  params: {
                    id: freebetData.external_id,
                    lang: $route.params.lang || 'en',
                    ...($route.params.skinFilt ||
                    $store.getters.skinStorage ||
                    freebetData.skin_id
                      ? {
                          skinFilt: (
                            $route.params.skinFilt ||
                            $store.getters.skinStorage ||
                            freebetData.skin_id
                          ).toString(),
                        }
                      : {}),
                  },
                }"
                class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:bg-gray-50 transition-all shadow-sm active:scale-95"
              >
                <PencilSquareIcon class="w-7 h-7" />
                <span>Edit</span>
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="freebetData?.freebetcurrencies?.length == 0"
        class="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3"
      >
        <ExclamationTriangleIcon class="w-5 h-5 text-amber-500" />
        <span class="text-amber-800 text-sm font-medium">
          To activate this freebet, you must associate at least one currency.
        </span>
      </div>
      <div class="lg:col-span-2 space-y-6">
        <div
          class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
        >
          <div
            class="px-3 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2"
          >
            <UsersIcon class="w-4 h-4 text-gray-400" />
            <h3
              class="font-bold text-gray-700 text-[11px] uppercase tracking-wider"
            >
              Usage Progress
            </h3>
          </div>
          <div class="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            <div
              class="flex justify-around md:col-span-1 border-r border-gray-50"
            >
              <div class="text-center">
                <p class="text-3xl font-semibold text-gray-900">
                  {{ freebetData.players_count }}
                </p>
                <p
                  class="text-[9px] text-gray-400 uppercase font-bold tracking-widest"
                >
                  Claimed
                </p>
              </div>
              <div class="text-center">
                <p class="text-3xl font-semibold text-slate-500">
                  {{ freebetData.max_players }}
                </p>
                <p
                  class="text-[9px] text-gray-400 uppercase font-bold tracking-widest"
                >
                  Total Limit
                </p>
              </div>
            </div>
            <div class="md:col-span-2">
              <div class="flex justify-between mb-2">
                <span class="text-xs font-bold text-gray-600"
                  >{{ playersProgress }}% Capacity Reached</span
                >
              </div>
              <div
                class="w-full bg-gray-100 rounded-full h-4 overflow-hidden p-0.5 border border-gray-200"
              >
                <div
                  class="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full transition-all duration-1000 shadow-sm"
                  :style="{ width: playersProgress + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex">
          <div class="space-y-6 min-w-full">
            <div
              class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div
                class="p-3 border-b border-gray-100 bg-gray-50/50 flex items-center gap-2"
              >
                <CubeIcon class="w-4 h-4 text-gray-400" />
                <h3
                  class="font-bold text-gray-700 text-[11px] uppercase tracking-wider"
                >
                  Configuration
                </h3>
              </div>
              <div>
                <div class="py-2.5 px-3 space-y-4">
                  <div
                    v-if="freebetData.campaign_id"
                    class="p-3 bg-blue-50/50 rounded-lg border border-blue-100"
                  >
                    <a
                      :href="`/slot/rains/${freebetData.campaign_id}`"
                      target="_blank"
                      class="text-blue-600 text-xs font-bold flex items-center gap-2 hover:underline"
                    >
                      Linked Campaign: #{{ freebetData.campaign_id }}
                      <i class="fa-solid fa-link"></i>
                    </a>
                  </div>
                  <div class="space-y-2">
                    <span
                      class="text-slate-600 text-[10px] font-bold uppercase tracking-tighter"
                      >Internal Note</span
                    >
                    <p
                      v-if="freebetData.note"
                      class="text-slate-600 text-xs italic leading-relaxed"
                    >
                      "{{ freebetData.note }}"
                    </p>
                    <div
                      v-else
                      class="text-slate-500 text-xs italic py-2 flex items-center gap-2"
                    >
                      No internal notes available
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="py-2.5 px-3 grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5"
              >
                <div class="flex flex-col gap-1">
                  <span
                    class="text-slate-600 text-[10px] font-bold uppercase tracking-tight"
                    >Mode</span
                  >
                  <span
                    class="font-bold text-gray-800 capitalize bg-green-100 px-2 py-1 rounded text-xs w-fit"
                  >
                    {{ checkMode(freebetData.mode) }}
                  </span>
                </div>

                <div class="flex flex-col gap-1">
                  <span
                    class="text-slate-600 text-[10px] font-bold uppercase tracking-tight"
                    >Total Quantity</span
                  >
                  <span class="font-bold text-gray-900 text-sm">
                    {{ freebetData.quantity }}
                  </span>
                </div>

                <div class="flex flex-col gap-1">
                  <span
                    class="text-slate-600 text-[10px] font-bold uppercase tracking-tight"
                    >Games</span
                  >
                  <div
                    class="flex items-start justify-start gap-1.5"
                    v-if="freebetData?.games?.length"
                  >
                    <span
                      v-for="(game, index) in freebetData.games"
                      :key="game"
                      class="font-bold text-gray-800 capitalize bg-green-100 px-2 py-1 rounded text-xs w-fit"
                    >
                      {{ game }}
                    </span>
                  </div>
                  <div class="flex items-start justify-start gap-1.5" v-else>
                    <span class="font-bold text-gray-900 text-sm">
                      All games
                    </span>
                  </div>
                </div>
              </div>
              <div
                class="p-3 grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <div
                  class="flex items-center gap-3 p-3 bg-green-50/30 rounded-lg border border-green-50"
                >
                  <div class="p-2 bg-green-100 rounded-lg shadow-inner">
                    <CalendarDaysIcon class="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p
                      class="text-[9px] uppercase font-bold text-gray-400 tracking-tighter"
                    >
                      Start Date
                    </p>
                    <p class="text-xs font-bold text-gray-800">
                      {{ formatDate(freebetData.start_at) }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-center gap-3 p-3 bg-red-50/30 rounded-lg border border-red-50"
                >
                  <div class="p-2 bg-red-100 rounded-lg shadow-inner">
                    <ClockIcon class="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p
                      class="text-[9px] uppercase font-bold text-gray-400 tracking-tighter"
                    >
                      End Date
                    </p>
                    <p class="text-xs font-bold text-gray-800">
                      {{ formatDate(freebetData.end_at) }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-center gap-3 p-3 bg-gray-50/50 rounded-lg border border-gray-100"
                >
                  <div
                    class="p-2 bg-white border border-gray-200 rounded-lg shadow-sm"
                  >
                    <PlusCircleIcon class="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p
                      class="text-[9px] uppercase font-bold text-gray-400 tracking-tighter"
                    >
                      Created On
                    </p>
                    <p class="text-xs font-medium text-gray-600">
                      {{ formatDate(freebetData.created_at) }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-center gap-3 p-3 bg-gray-50/50 rounded-lg border border-gray-100"
                >
                  <div
                    class="p-2 bg-white border border-gray-200 rounded-lg shadow-sm"
                  >
                    <PencilIcon class="w-4 h-4 text-gray-400" />
                  </div>
                  <div>
                    <p
                      class="text-[9px] uppercase font-bold text-gray-400 tracking-tighter"
                    >
                      Last Modified
                    </p>
                    <p class="text-xs font-medium text-gray-600">
                      {{ formatDate(freebetData.updated_at) }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
        >
          <div
            class="px-3 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between"
          >
            <div class="flex items-center gap-2">
              <CurrencyDollarIcon class="w-4 h-4 text-gray-400" />
              <h3
                class="font-bold text-gray-700 text-[11px] uppercase tracking-wider"
              >
                Currency(s) ({{ freebetData.freebetcurrencies?.length || 0 }})
              </h3>
            </div>
          </div>

          <div class="p-6">
            <div
              v-if="freebetData?.freebetcurrencies?.length > 0"
              class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
            >
              <div
                v-for="currency in freebetData.freebetcurrencies"
                :key="currency._id"
                class="bg-white border border-gray-100 rounded-xl p-3 shadow-sm hover:border-indigo-200 transition-all group relative overflow-hidden"
              >
                <div class="flex justify-between items-center mb-3">
                  <span class="text-lg font-black text-gray-800">{{
                    currency.currency_code
                  }}</span>
                </div>
                <div class="space-y-2 relative z-10">
                  <div class="flex justify-between text-[13px]">
                    <span class="text-slate-600">Amount</span>
                    <span class="font-bold text-gray-900">{{
                      parseNumber(currency?.amount)
                    }}</span>
                  </div>

                  <div
                    class="flex justify-between text-[13px] border-t border-gray-50 pt-2"
                  >
                    <span class="text-slate-600">Min Balance Req.</span>
                    <span class="font-bold text-gray-700">{{
                      parseNumber(currency.min_balance)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-else
              class="text-center py-10 border-2 border-dashed border-gray-100 rounded-xl"
            >
              <p class="text-gray-400 text-xs italic">
                No currencies linked to this promotion.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-else-if="!isLoadingDetail && showError"
      class="max-w-md mx-auto mt-20 text-center p-8 bg-white rounded-2xl border border-red-100 shadow-xl"
    >
      <ExclamationTriangleIcon class="w-12 h-12 text-red-400 mx-auto mb-4" />
      <h2 class="text-lg font-black text-gray-800 mb-2">Data Load Error</h2>
      <p class="text-gray-500 text-sm mb-6">
        We encountered a problem fetching the freebet details. Please try again.
      </p>
      <button
        @click="window.location.reload()"
        class="px-8 py-2.5 bg-gray-900 text-white text-sm font-bold rounded-xl hover:bg-black transition-all shadow-lg active:scale-95"
      >
        Retry Fetch
      </button>
    </div>

    <FreebetCampaignClone
      v-if="isCloningFreebets"
      :detail="freebetClone"
      :closeFreebetsClone="closeFreebetsClone"
      class="fixed inset-0 z-50 overflow-y-auto"
    />
  </div>
</template>

<script>
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { getFetch, putFetch } from "@/api/axiosClient";
import Loader from "../Loader.vue";
import FreebetCampaignClone from "../FreebetCampaignClone.vue";
import Swal from "sweetalert2";
import SummaryCampaign from "../SummaryCampaign.vue";
import IconWrapper from "../Utility/IconWrapper.vue";
import { mapGetters } from "vuex";
import { formatString } from "@/helpers/date.js";
import {
  CubeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  CalendarIcon,
  CalendarDaysIcon,
  ClockIcon,
  PlusCircleIcon,
  PencilIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
} from "@heroicons/vue/24/outline";
import store from "@/store/index";
export default {
  name: "FreebetInfo",
  props: {
    betId: {
      type: String,
      required: true,
    },
    saveDetail: Function,
  },
  components: {
    FlatPickr,
    CubeIcon,
    UsersIcon,
    CurrencyDollarIcon,
    CalendarIcon,
    CalendarDaysIcon,
    ClockIcon,
    PlusCircleIcon,
    PencilIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    ExclamationTriangleIcon,
    Loader,
    FreebetCampaignClone,
    IconWrapper,
    SummaryCampaign,
    PencilSquareIcon,
    DocumentDuplicateIcon,
  },
  data() {
    return {
      cardsData: [],
      dataLoaded: false,
      isLoadingDetail: false,
      freebetData: {},
      freebetClone: {},
      gameChoose: {},
      showError: false,
      savingNewDetail: false,
      isCurrencyOpen: false,
      isCloningFreebets: false,
      selectedDatabase: "theearcasino",
    };
  },
  watch: {
    freebetData: "getCost",
  },
  computed: {
    ...mapGetters([
      "allowedSkins",
      "isAdmin",
      "allowedGamesFreebet",
      "crashGameOptions",
      "plinkoGameOptions",
      "crashGamesList",
      "plinkoGamesList",
    ]),
    playersProgress() {
      if (!this.freebetData || this.freebetData.max_players === 0) return 0;
      return parseFloat(
        (this.freebetData.players_count / this.freebetData.max_players) * 100,
      ).toFixed(2);
    },
    formattedTypes() {
      if (!this.freebetData?.type) return [];

      const rawParts = this.freebetData.type.split("_");

      const keepTogether = ["cashout_1", "cashout_2"];

      const labelMap = {
        oddeven: "Odd/Even",
        ranges: "Ranges",
        cashout_1: "Cashout 1",
        cashout_2: "Cashout 2",
      };

      const result = [];
      let i = 0;

      while (i < rawParts.length) {
        const maybeCombo = `${rawParts[i]}_${rawParts[i + 1]}`;

        if (keepTogether.includes(maybeCombo)) {
          result.push({
            value: maybeCombo,
            label:
              labelMap[maybeCombo] ||
              maybeCombo.replace(/_/g, " ").toUpperCase(),
          });
          i += 2;
        } else {
          const part = rawParts[i];
          result.push({
            value: part,
            label: labelMap[part] || part.toUpperCase(),
          });
          i += 1;
        }
      }

      return result;
    },
  },
  methods: {
    checkMode(mode) {
      if (mode?.toLowerCase() === "rain") return mode;
      if (mode) return mode.replace("_", " ");
    },
    formatDate(dateString, onlyYMD = false) {
      return formatString(dateString);
    },
    parseNumber(amount) {
      const num = parseFloat(amount);
      return isNaN(num) ? "- -" : num.toFixed(2);
    },
    cloneFreebets() {
      this.isCloningFreebets = true;
      this.freebetClone = { ...this.freebetData };
      document.body.style.overflow = "hidden";
    },
    editRow() {
      const id = this.freebetData.external_id;
      const lang = this.$route.params.lang || "en";
      const skin =
        this.$route.params.skinFilt || this.$store.getters.skinStorage;
      this.$router.push({
        name: "FreebetsEdit",
        params: {
          lang,
          ...(skin ? { skinFilt: skin.toString() } : {}),
          id: id,
        },
      });
    },
    closeFreebetsClone() {
      this.isCloningFreebets = false;
      document.body.style.overflow = "";
    },
    async getCost() {
      return;
      if (this.freebetData?.skin_id && this.freebetData?.external_id) {
        try {
          let query = {
            skin_id: this.freebetData?.skin_id,
          };

          if (this.freebetData?.external_id) {
            query.freebet_id = this.freebetData?.external_id;
          }

          let data = await getFetch("/freebets/calculate-costs", query);

          this.cardsData = data;
          this.dataLoaded = true;
        } catch (error) {
          console.error("Error fetching cost data:", error);
          this.dataLoaded = true;
        }
      }
    },
  },
  async mounted() {
    await store.dispatch("getGame", { endpoint: "", params: {} });
    this.gameChoose = this.allowedGamesFreebet;

    this.isLoadingDetail = true;
    try {
      const response = await getFetch("freebets/" + this.betId, {});
      this.showError = false;
      this.freebetData = response;
      this.saveDetail(this.freebetData);
    } catch (error) {
      this.showError = true;
      console.error("Error loading freebet data:", error);
    } finally {
      this.isLoadingDetail = false;
    }
  },
};
</script>
